import { observable, configure, action, computed, toJS } from 'mobx';
import { route } from 'preact-router';

import util from '../lib/util';

configure({ enforceActions: 'always' });

class StoreModel {
    constructor(name, opts) {
        this.name = name;
        this.namePlural = opts.namePlural;
        this.api = opts.api;
        this.opts = opts;
    }

    @observable queryFilter = util.getObject(`${this.name}QueryFilter`) || this.opts.queryFilter || {};

    @observable sort = this.opts.sort;

    @observable defaultSort = this.opts.sort;

    @observable extendedView = this.opts.extendedView;

    @observable limit = 50;

    @observable offset = 0;

    @observable searchResults = [];

    @observable totalSearch = 0;

    @observable searchSelectId = 0;

    @observable searchSelectIdx = 0;

    @observable total = 0;

    @observable totalAppend = 0;

    @observable saved = {};

    @observable view = {};

    @action
    update(items, append) {
        if (append) {
            this[this.namePlural] = this[this.namePlural].concat(items);
        } else {
            this[this.namePlural] = items;
        }
    }

    @action
    deleteElement(val, field = 'id') {
        const idx = this[this.namePlural].findIndex(e => e[field] === val);
        if (idx >= 0) {
            this[this.namePlural] = this[this.namePlural].filter(e => e[field] !== val);
        }
    }

    @action
    updateKeyValue(key, value, opt = {}) {
        this[key] = value;
        if (opt.save) {
            util.set(`${this.name}${key}`, value);
        }
    }

    @action
    updateItem(item) {
        this[this.name] = item;
    }

    @action
    updateField(id, field, value, findBy = 'id') {
        const idx = this[this.namePlural].findIndex(e => e[findBy] === id);
        if (idx >= 0) {
            const obj = this[this.namePlural][idx];
            util.setNestedValue(obj, field, value);
        }
        // else {
        //     const obj = this[this.namePlural];
        //     util.setNestedValue(obj, field, value);
        // }
        if (this[this.name]) {
            util.setNestedValue(this[this.name], field, value);
        }
    }

    @action
    updateQueryFilter(queryFilter) {
        this.queryFilter = queryFilter;
        util.setObject(`${this.name}QueryFilter`, this.queryFilter);
    }

    @action
    updateSort(sort) {
        this.sort = sort;
    }

    @action
    resetSort() {
        this.sort = this.defaultSort;
        this.load();
    }

    @action
    toggleSort(sort, initial = '') {
        const hasSign = this.sort.match(/^-/);
        const oldSort = this.sort.replace(/^-/, '');
        if (oldSort === sort) {
            if (hasSign) {
                this.sort = sort;
            } else {
                this.sort = `-${sort}`;
            }
        } else {
            this.sort = `${initial}${sort}`;
        }
        this.load();
    }

    @action
    updateOffset(offset) {
        this.offset = offset;
    }

    @action
    updateTotal(total) {
        this.total = total;
    }

    @action
    updateTotalAppend(total) {
        this.totalAppend = total;
    }

    @action
    updateSaved(key, val) {
        if (val) {
            this.saved[key] = val;
        } else {
            delete this.saved[key];
        }
    }

    @action
    toggleView(element, value) {
        this.view[element] = value || !this.view[element];
    }

    @action
    resetQueryFilter() {
        this.updateQueryFilter({});
    }

    @action
    toggleQueryfilter = (filter, value = 1, opt = {}) => {
        const finalFilter = { ...this.queryFilter };
        if (opt.setValue) {
            finalFilter[filter] = value;
        } else {
            finalFilter[filter] = finalFilter[filter] ? null : value;
        }
        if (!finalFilter[filter] && finalFilter[filter] !== 0) {
            delete finalFilter[filter];
        }
        this.updateQueryFilter(finalFilter);
        if (!opt.skipUpdate) {
            this.list();
            if (typeof this.tree === 'function') {
                this.tree();
            }
        }
        if (opt.viewElement) {
            opt.toggleView(opt.viewElement);
        }
        if (opt.setTimer) {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(async () => {
                this.list();
                if (typeof this.tree === 'function') {
                    this.tree();
                }
            }, 750);
        }
        if (typeof opt.toggle === 'function') {
            opt.toggle(value);
        }
        if (opt.route) {
            route(opt.route, true);
        }
    }

    @action
    updateSearchSelectId(id) {
        this.searchSelectId = id;
    }

    @action
    updateSearchSelectIdx(idx) {
        this.searchSelectIdx = idx;
    }

    @action
    updateSearchResults(items) {
        this.searchResults = items;
    }

    @action
    updateSearchTotal(total) {
        this.totalSearch = total;
    }

    @action
    incSearchSelectIdx() {
        this.searchSelectIdx += 1;
        if (this.searchSelectIdx >= this.totalSearch) {
            this.searchSelectIdx = this.totalSearch - 1;
        }
        const item = { ...this.searchResults[this.searchSelectIdx] };
        this.updateSearchSelectId(item.id);
    }

    @action
    decSearchSelectIdx() {
        this.searchSelectIdx -= 1;
        if (this.searchSelectIdx < 0) {
            this.searchSelectIdx = 0;
        }
        const item = { ...this.searchResults[this.searchSelectIdx] };
        this.updateSearchSelectId(item.id);
    }

    @action
    resetSearch() {
        this.updateSearchResults([]);
        this.updateSearchTotal(0);
        this.updateSearchSelectId(0);
        this.searchSelectIdx = 0;
    }

    @action
    addElement(idx, dataObj, field) {
        let element = {};
        if (typeof idx === 'object') {
            element = { ...idx };
        } else {
            element = this.getElement(idx);
        }
        if (typeof this[dataObj][field] === 'undefined') {
            this[dataObj][field] = [];
        }
        const elIdx = this[dataObj][field].findIndex(e => parseInt(e.id, 10) === element.id);
        if (elIdx < 0) {
            this[dataObj][field].push(element);
            this.save({
                [field]: this[dataObj][field],
            });
        }
    }

    getElement(idx) {
        const index = idx || this.searchSelectIdx;
        return {
            id: this.searchResults[index].id,
            title: this.searchResults[index].title,
        };
    }

    @action
    removeElement(id, dataObj, field) {
        const elId = parseInt(id, 10);
        const elIdx = this[dataObj][field].findIndex(e => parseInt(e.id, 10) === elId);
        if (elIdx >= 0) {
            this[dataObj][field] = this[dataObj][field].filter(e => parseInt(e.id, 10) != elId);
            // this[dataObj][field].splice(elIdx, 1); // Not detected by observers.
            this.save({
                [field]: this[dataObj][field],
            });
        }
    }

    async search(searchText, options = {}) {
        const url = util.getNestedValue(this, 'api.search.url');
        const apiParams = util.getNestedValue(this, 'api.search.params');
        const response = await util.fetchApi(url, { publish: true }, {
            sort: this.sort,
            limit: this.limit,
            ...apiParams,
            ...options,
            search: searchText,
        });
        if (response.data) {
            this.updateSearchSelectId(util.asNumber(response, 'data', 0, 'id'));
            this.updateSearchSelectIdx(0);
            this.updateSearchResults(response.data);
            this.updateSearchTotal(response.total);
        }
    }

    makeQuery() {
        const query = toJS(this.queryFilter);
        return query;
    }

    async load(id = '', append = false, opt = {}) {
        if (!append) {
            this.updateOffset(0);
        }
        const url = util.getNestedValue(this, 'api.load.url');
        const qf = opt.skipFilter ? {} : this.makeQuery();
        let queryFilter = id ? {} : { ...qf };
        if (opt.query) {
            queryFilter = opt.query;
        }
        const response = await util.fetchApi(`${url}${id}`, { publish: true }, util.cleanObject({
            extendedView: this.extendedView,
            offset: this.offset,
            limit: opt.limit || this.limit,
            sort: opt.sort || this.sort,
            ...queryFilter,
        }));
        if (response.status < 400) {
            if (Array.isArray(opt.addData)) {
                opt.addData.forEach((el) => {
                    if (response[el]) {
                        this.updateKeyValue(el, response[el]);
                    }
                });
            }
            if (id) {
                if (Array.isArray(response.data)) {
                    this.updateItem(response.data[0]);
                } else {
                    this.updateItem(response.data);
                }
            } else if (opt.dataKey) {
                this.updateKeyValue(opt.dataKey, response.data);
            } else {
                this.update(response.data, append);
                if (append) {
                    this.updateTotalAppend(response.data.length);
                } else {
                    this.updateTotal(response.total);
                    window.scrollTo(0, 0);
                }
            }
            if (typeof this.parseElements === 'function') {
                this.parseElements();
            }
        }
    }

    async list(append, opt = {}) {
        return this.load('', append, opt);
    }

    async saveField(id, field, value, updateMemory) {
        const url = util.getNestedValue(this, 'api.save.url');
        if (updateMemory) {
            this.updateField(id, field, value);
        }
        this.updateSaved(`${field}.${id}`, true);
        const response = await util.fetchApi(`${url}${id}`, {
            publish: true,
            method: 'PATCH',
        }, {
            [field]: value,
        });
        if (response.status < 300) {
            // Remove saved
            setTimeout(() => {
                this.updateSaved(`${field}.${id}`);
            }, 2000);
        }
    }

    /**
     * Update many database entries in one operation.
     *
     * @param {object} filter - The query filter for this update.
     * @param {string} field - Field name.
     * @param {*} value - Field value.
     * @param {boolean} updateMemory - Setting to update memory or not.
     * @memberof StoreModel
     */
    async updateMany(filter, field, value, updateMemory) {
        const url = util.getNestedValue(this, 'api.save.url');
        if (updateMemory) {
            this.updateField(filter.id, field, value);
        }
        this.updateSaved(`${field}.${filter.id}`, true);
        const response = await util.fetchApi(`${url}${filter.id}`, {
            publish: true,
            method: 'PATCH',
        }, {
            ...filter,
            [field]: value,
        });
        if (response.status < 300) {
            // Remove saved
            setTimeout(() => {
                this.updateSaved(`${field}.${filter.id}`);
            }, 2000);
        }
    }

    async save(data) {
        const url = util.getNestedValue(this, 'api.save.url');
        await util.fetchApi(`${url}${this[this.name].id}`,
            { publish: true, method: 'PATCH' }, {
                ...data,
            });
    }

    async create(data) {
        const url = util.getNestedValue(this, 'api.save.url');
        await util.fetchApi(`${url}`,
            { publish: true, method: 'POST' }, {
                ...data,
            });
    }

    async delete(id, field = 'id', data = {}) {
        this.deleteElement(id, field);
        const url = util.getNestedValue(this, 'api.delete.url');
        await util.fetchApi(`${url}${id}`,
            { publish: true, method: 'DELETE' }, {
                ...data,
            });
    }
}

export default StoreModel;




// WEBPACK FOOTER //
// ./stores/storeModel.js